import React from 'react';

type SelectOption = {
  identifier: string;
  label: string;
};

type DynamicChoice = {
  choices: SelectOption[];
  choices_requirements: Array<{
    previous_associated_key: string;
    previous_raw_answer: string[];
    operator: string;
  }>;
};

type SelectProps = {
  label: string;
  options: SelectOption[];
  helperText?: string;
  required?: boolean;
  error?: string;
  value?: string;
  onChange: (value: string) => void;
  dynamicChoices?: DynamicChoice[];
  formData?: Record<string, any>;
  className?: string;
};

export function Select({
  label,
  options: defaultOptions,
  helperText,
  required,
  error,
  value,
  onChange,
  dynamicChoices = [],
  formData = {},
  className = ''
}: SelectProps) {
  // Get the current options based on dynamic rules
  const currentOptions = React.useMemo(() => {
    if (!dynamicChoices.length) {
      return defaultOptions;
    }

    // Find the first matching dynamic choice based on requirements
    const matchingChoice = dynamicChoices.find(choice => 
      choice.choices_requirements.every(requirement => {
        const currentValue = formData[requirement.previous_associated_key];
        switch (requirement.operator) {
          case 'REQUIREMENT_OPERATOR_EQUAL':
            return requirement.previous_raw_answer.includes(currentValue);
          case 'REQUIREMENT_OPERATOR_IS_ANSWERED':
            return currentValue !== undefined && currentValue !== '';
          case 'REQUIREMENT_OPERATOR_IS_NOT_ANSWERED':
            return currentValue === undefined || currentValue === '';
          case 'REQUIREMENT_OPERATOR_INFERIOR_STRICT':
            return Number(currentValue) < Number(requirement.previous_raw_answer[0]);
          default:
            return false;
        }
      })
    );

    return matchingChoice ? matchingChoice.choices : defaultOptions;
  }, [defaultOptions, dynamicChoices, formData]);

  const selectClasses = `block w-full rounded-md border-gray-300 shadow-xs 
    focus:border-blue-500 focus:ring-blue-500 p-2
    ${error ? 'border-red-300' : ''} 
    ${className}`;

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </label>
      
      <select 
        className={selectClasses}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
      >
        <option value="">Sélectionner</option>
        {currentOptions.map((option) => (
          <option key={option.identifier} value={option.identifier}>
            {option.label}
          </option>
        ))}
      </select>

      {helperText && (
        <p className="mt-1 text-sm text-gray-500">{helperText}</p>
      )}
      {/* {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )} */}
    </div>
  );
} 